/**
 * Modèle de l'éditeur Lens — PUR (aucun React, aucun fetch), donc testable.
 *
 * Une visualisation est un petit état déclaratif :
 *   { type, x, y: [metric…], breakdown }
 * où chaque dimension vaut { id, op, field, params, label? }.
 *
 * Ce module en tire les DEUX productions de l'éditeur :
 *   - buildQuery()  → la requête d'agrégation Elasticsearch (DSL), envoyée telle
 *                     quelle à la route brute d'elasticsearch_endpoint ;
 *   - buildBlock()  → le bloc reactive-json qui la rend : un `ChartLoader`
 *                     (primitive existante, Chart.js) et son `shape:`. C'est le
 *                     même bloc que l'aperçu monte en direct et que l'export
 *                     donne à coller dans une page ou un tableau de bord.
 *
 * Les champs sont ceux que sert /api/orbit/lens/indexes/{index}/fields :
 *   { name, aggField, kind: date|number|string|boolean|text, label }.
 */

/* ------------------------------------------------------------------ *
 * Fonctions
 * ------------------------------------------------------------------ */

export const METRIC_OPS = {
  count: { label: 'Nombre' },
  unique: { label: 'Valeurs uniques', kinds: ['string', 'number', 'boolean', 'date'] },
  avg: { label: 'Moyenne', kinds: ['number'] },
  sum: { label: 'Somme', kinds: ['number'] },
  min: { label: 'Minimum', kinds: ['number'] },
  max: { label: 'Maximum', kinds: ['number'] },
  median: { label: 'Médiane', kinds: ['number'] },
  pct: { label: 'Percentile', kinds: ['number'] },
  last: { label: 'Dernière valeur', kinds: ['number'] },
}

export const BUCKET_OPS = {
  date_histogram: { label: 'Histogramme de dates', kinds: ['date'] },
  terms: { label: 'Valeurs principales', kinds: ['string', 'number', 'boolean'] },
  histogram: { label: 'Intervalles', kinds: ['number'] },
}

/** Colonne d'une LISTE de documents : la valeur brute du champ, de tout genre. */
export const COLUMN_OPS = {
  field: { label: 'Valeur du champ', kinds: ['string', 'number', 'boolean', 'date', 'text'] },
}

export const INTERVALS = {
  auto: 'Automatique',
  hour: 'Heure',
  '3h': '3 heures',
  day: 'Jour',
  week: 'Semaine',
  month: 'Mois',
  year: 'Année',
}

/**
 * Graduation de l'axe des dates, indépendante du pas des points : des points
 * par jour, un repère par année (ChartJs `xTickUnit`).
 */
export const TICK_UNITS = {
  auto: 'Automatique',
  month: 'Un repère par mois',
  year: 'Un repère par année',
}

/** Période globale (filtre sur le champ de temps). */
export const RANGES = {
  '24h': 'Dernières 24 h',
  '7d': '7 derniers jours',
  '30d': '30 derniers jours',
  '90d': '90 derniers jours',
  '1y': '12 derniers mois',
  '3y': '3 dernières années',
  '5y': '5 dernières années',
  '10y': '10 dernières années',
  all: 'Toute la période',
}
const RANGE_MS = { '24h': 864e5, '7d': 7 * 864e5, '30d': 30 * 864e5, '90d': 90 * 864e5, '1y': 365 * 864e5, '3y': 3 * 365 * 864e5, '5y': 5 * 365 * 864e5, '10y': 10 * 365 * 864e5 }

export const TYPES = {
  bar: 'Barres verticales',
  bar_h: 'Barres horizontales',
  line: 'Lignes',
  doughnut: 'Anneau',
  metric: 'Métrique',
  table: 'Tableau',
  list: 'Liste',
}

export const SLOTS = {
  bar: [
    { key: 'x', label: 'Axe horizontal', kind: 'bucket' },
    { key: 'y', label: 'Axe vertical', kind: 'metric', multi: true, req: true },
    { key: 'breakdown', label: 'Ventilation', kind: 'bucket' },
  ],
  bar_h: [
    { key: 'x', label: 'Axe vertical', kind: 'bucket' },
    { key: 'y', label: 'Axe horizontal', kind: 'metric', multi: true, req: true },
    { key: 'breakdown', label: 'Ventilation', kind: 'bucket' },
  ],
  line: [
    { key: 'x', label: 'Axe horizontal', kind: 'bucket' },
    { key: 'y', label: 'Axe vertical', kind: 'metric', multi: true, req: true },
    { key: 'breakdown', label: 'Ventilation', kind: 'bucket' },
  ],
  doughnut: [
    { key: 'x', label: 'Découper par', kind: 'bucket', req: true },
    { key: 'y', label: 'Taille des parts', kind: 'metric', req: true },
  ],
  metric: [
    { key: 'y', label: 'Métriques', kind: 'metric', multi: true, req: true },
  ],
  table: [
    { key: 'x', label: 'Lignes', kind: 'bucket' },
    { key: 'breakdown', label: 'Colonnes', kind: 'bucket' },
    { key: 'y', label: 'Métriques', kind: 'metric', multi: true, req: true },
  ],
  // Les DOCUMENTS eux-mêmes, sans agrégation : une ligne par document, une
  // colonne par champ choisi — ou, en fiche, une ligne par champ.
  list: [
    { key: 'columns', label: 'Colonnes', kind: 'column', multi: true, req: true },
  ],
}

/** Les emplacements qui portent une LISTE de dimensions. */
const MULTI_KEYS = ['y', 'columns']

/* ------------------------------------------------------------------ *
 * Dimensions
 * ------------------------------------------------------------------ */

let NID = 1
const newId = () => `d${Date.now().toString(36)}${(NID++).toString(36)}`

export const mk = (op, field, params = {}) => ({ id: newId(), op, field, params })

/** Index des champs par nom. */
export const fieldMap = (fields) => new Map((fields || []).map((f) => [f.name, f]))

const kindOf = (fields, name) => fieldMap(fields).get(name)?.kind

/** Champ utilisable dans une agrégation (un `text` sans sous-champ keyword ne l'est pas). */
export const isAggregatable = (f) => Boolean(f && f.aggField && f.kind !== 'text')

export const defMetric = (fields, name) => {
  const k = kindOf(fields, name)
  if (k === 'number') return mk('avg', name)
  if (k === 'string' || k === 'boolean') return mk('unique', name)
  return mk('count', null)
}

export const defBucket = (fields, name) => {
  const k = kindOf(fields, name)
  if (k === 'date') return mk('date_histogram', name, { interval: 'auto' })
  if (k === 'number') return mk('histogram', name, { interval: 'auto' })
  return mk('terms', name, { size: 5, orderBy: 'count', other: false })
}

const fieldTitle = (fields, name) => {
  const f = fieldMap(fields).get(name)
  return f?.label || name
}

export function dimLabel(d, fields = []) {
  if (!d) return ''
  if (d.label) return d.label
  const f = fieldTitle(fields, d.field)
  if (d.op === 'count') return "Nombre d'enregistrements"
  if (d.op === 'pct') return `Percentile ${d.params?.p || 95} de ${f}`
  if (METRIC_OPS[d.op]) return `${METRIC_OPS[d.op].label} de ${f}`
  if (d.op === 'terms') return `Top ${d.params?.size || 5} ${f}`
  if (d.op === 'field') return f
  return f
}

const INTEGER_TYPES = ['integer', 'long', 'short', 'byte', 'unsigned_long']

/**
 * Décimales affichées pour une métrique : celles qu'on lui a fixées
 * (`params.decimals`), sinon 0 pour un compte ou pour le min / max / somme
 * d'un champ ENTIER (un SRI, une année), et 2 pour le reste (une moyenne, une
 * performance en %, une VL).
 */
export function decimalsOf(m, fields = []) {
  const set = m.params?.decimals
  if (set !== undefined && set !== null && set !== '' && Number.isFinite(Number(set))) return Math.max(0, Math.min(6, Number(set)))
  if (['count', 'unique'].includes(m.op)) return 0
  const esType = fieldMap(fields).get(m.field)?.esType
  return ['min', 'max', 'sum'].includes(m.op) && INTEGER_TYPES.includes(esType) ? 0 : 2
}

/** Une métrique ADDITIVE : son total a un sens (« Total du rapport », « Autres »). */
const isAdditive = (m) => m.op === 'count' || m.op === 'sum'

/** Les métriques complètes (une métrique sans champ, hors `count`, est ignorée). */
export const metricsOf = (viz) => (viz.y || []).filter((d) => d.op === 'count' || d.field)

/* ------------------------------------------------------------------ *
 * Emplacements et placement automatique (glisser sur l'espace de travail)
 * ------------------------------------------------------------------ */

export const getSlot = (v, k) => (MULTI_KEYS.includes(k) ? (v[k] || []) : v[k] ? [v[k]] : [])
export const setSlot = (v, k, arr) => (MULTI_KEYS.includes(k) ? { ...v, [k]: arr } : { ...v, [k]: arr[0] || null })

const autoCount = () => ({ ...mk('count', null), auto: true })

export function emptyViz(timeField) {
  return {
    type: 'bar',
    x: timeField ? mk('date_histogram', timeField, { interval: 'auto' }) : null,
    y: [autoCount()],
    breakdown: null,
  }
}

/** Ce qu'un type ne sait pas représenter est retiré ou déplacé. */
export function switchType(v, t, timeField) {
  if (t === v.type) return v
  const n = { ...v, type: t }
  // Vers une liste : les champs déjà en jeu deviennent ses premières colonnes.
  if (t === 'list') {
    const names = [v.x, v.breakdown, ...(v.y || [])].map((d) => d?.field).filter(Boolean)
    n.columns = v.columns?.length ? v.columns : [...new Set(names)].map((name) => mk('field', name))
    return n
  }
  if (v.type === 'list' && !(v.y || []).length) n.y = [autoCount()]
  if (t === 'metric') {
    n.x = null
    n.breakdown = null
  }
  else if (t === 'doughnut') {
    n.y = v.y.slice(0, 1)
    // Un anneau découpe UNE dimension : la ventilation, si elle existe, est la
    // meilleure candidate (les parts d'un histogramme de dates se lisent mal).
    if (v.breakdown) n.x = v.breakdown
    n.breakdown = null
  }
  else if (v.type === 'metric' && !v.x && timeField) {
    n.x = mk('date_histogram', timeField, { interval: 'auto' })
  }
  return n
}

export function placeOnWorkspace(v, fields, name, timeField) {
  const k = kindOf(fields, name)
  const n = { ...v }
  if (n.type === 'list') return { ...n, columns: [...(n.columns || []), mk('field', name)] }
  const onlyAutoCount = n.y.length === 1 && n.y[0].op === 'count' && n.y[0].auto
  if (k === 'number') {
    const m = defMetric(fields, name)
    n.y = onlyAutoCount || n.type === 'doughnut' ? [m] : [...n.y, m]
    if (!n.x && n.type !== 'metric' && timeField) n.x = mk('date_histogram', timeField, { interval: 'auto' })
  }
  else if (k === 'date') {
    n.x = defBucket(fields, name)
    if (n.type === 'metric' || n.type === 'doughnut') n.type = 'line'
    if (!n.y.length) n.y = [autoCount()]
  }
  else {
    if (!n.y.length) n.y = [autoCount()]
    if (n.type === 'metric') {
      n.type = 'bar'
      n.x = defBucket(fields, name)
    }
    else if (!n.x || n.type === 'doughnut') {
      n.x = defBucket(fields, name)
      if (n.type === 'line') n.type = 'bar'
    }
    else if (!n.breakdown && n.x.field !== name) {
      n.breakdown = { ...defBucket(fields, name), params: { size: 3, orderBy: 'count', other: true } }
    }
    else {
      n.x = defBucket(fields, name)
    }
  }
  return n
}

export function placeOnSlot(v, fields, slot, name) {
  const d = slot.kind === 'column' ? mk('field', name) : slot.kind === 'metric' ? defMetric(fields, name) : defBucket(fields, name)
  const kept = slot.multi ? getSlot(v, slot.key).filter((x) => !x.auto) : []
  let n = setSlot(v, slot.key, [...kept, d])
  if (slot.kind === 'bucket' && !n.y.length) n = { ...n, y: [autoCount()] }
  return n
}

export const missingSlots = (v) => SLOTS[v.type].filter((s) => s.req && !getSlot(v, s.key).length)

export function vizTitle(v, fields) {
  if (v.type === 'list') {
    const cols = (v.columns || []).filter((d) => d.field)
    if (!cols.length) return 'Nouvelle liste'
    return cols.length > 4 ? `Liste de ${cols.length} champs` : `Liste : ${cols.map((d) => dimLabel(d, fields)).join(', ')}`
  }
  const ms = metricsOf(v)
  if (!ms.length) return 'Nouvelle visualisation'
  let t = ms.map((m) => dimLabel(m, fields)).join(', ')
  if (v.x) t += ` par ${(dimLabel(v.x, fields).trim() || fieldTitle(fields, v.x.field)).toLowerCase()}`
  if (v.breakdown) t += `, ventilé par ${fieldTitle(fields, v.breakdown.field).toLowerCase()}`
  return t
}

/* ------------------------------------------------------------------ *
 * Requête Elasticsearch
 * ------------------------------------------------------------------ */

const niceStep = (x) => {
  if (!(x > 0)) return 1
  const p = 10 ** Math.floor(Math.log10(x))
  const f = x / p
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p
}

/** Intervalle « automatique » d'un histogramme de dates, selon l'étendue couverte. */
export function autoInterval(spanMs) {
  if (!(spanMs > 0)) return 'month'
  if (spanMs <= 2 * 864e5) return 'hour'
  if (spanMs <= 8 * 864e5) return '3h'
  if (spanMs <= 92 * 864e5) return 'day'
  if (spanMs <= 2 * 365 * 864e5) return 'week'
  if (spanMs <= 8 * 365 * 864e5) return 'month'
  return 'year'
}

// Le LIBELLÉ de chaque barre est mis en forme par Elasticsearch lui-même
// (`format` + `time_zone` de l'agrégation) : la clé `key_as_string` arrive
// lisible, et le `shape:` du ChartLoader n'a rien à convertir.
const DATE_INTERVALS = {
  hour: { fixed_interval: '1h', format: "dd/MM HH'h'" },
  '3h': { fixed_interval: '3h', format: "dd/MM HH'h'" },
  day: { calendar_interval: 'day', format: 'dd/MM/yyyy' },
  week: { calendar_interval: 'week', format: 'dd/MM/yyyy' },
  month: { calendar_interval: 'month', format: 'MM/yyyy' },
  year: { calendar_interval: 'year', format: 'yyyy' },
}

export const TIME_ZONE = 'Europe/Paris'

/**
 * Étendue (ms) couverte par la requête : celle de la période, sinon celle du
 * champ (stats chargées par l'éditeur).
 */
function spanOf(field, ctx) {
  if (ctx.range && ctx.range !== 'all' && field === ctx.timeField) return RANGE_MS[ctx.range]
  const s = ctx.stats?.[field]
  return s && s.max != null && s.min != null ? s.max - s.min : (RANGE_MS[ctx.range] || 0)
}

/** Les champs dont une dimension « automatique » a besoin des bornes (stats). */
export function statsNeeded(viz) {
  return [viz.x, viz.breakdown]
    .filter((d) => d && d.params?.interval === 'auto' && (d.op === 'histogram' || d.op === 'date_histogram'))
    .map((d) => d.field)
}

function metricAgg(m, fmap, ctx = {}) {
  const field = fmap.get(m.field)?.aggField
  switch (m.op) {
    case 'count': return null
    case 'unique': return field ? { cardinality: { field } } : null
    case 'avg': case 'sum': case 'min': case 'max':
      return field ? { [m.op]: { field } } : null
    case 'median': return field ? { percentiles: { field, percents: [50], keyed: false } } : null
    case 'pct': return field ? { percentiles: { field, percents: [Number(m.params?.p) || 95], keyed: false } } : null
    // La valeur à la date la plus récente du bucket (une VL, une base 100 en fin
    // de semaine) : la date du champ temporel la plus grande, puis le max de ses
    // documents. Pas `top_metrics` : sa réponse est indexée par le NOM du champ,
    // qu'un chemin à points ne sait pas lire (`object_numbers.rank`).
    case 'last': {
      const time = fmap.get(ctx.timeField)?.aggField
      return field && time ? { terms: { field: time, size: 1, order: { _key: 'desc' } }, aggs: { v: { max: { field } } } } : null
    }
    default: return null
  }
}

/** Chemin de la valeur d'une métrique DANS un bucket. */
export function metricPath(m, i) {
  if (m.op === 'count') return 'doc_count'
  if (m.op === 'median' || m.op === 'pct') return `m${i}.values.0.value`
  if (m.op === 'last') return `m${i}.buckets.0.v.value`
  return `m${i}.value`
}

const singleValue = (m) => ['unique', 'avg', 'sum', 'min', 'max'].includes(m.op)

function bucketAgg(d, fmap, ctx, orderable) {
  const field = fmap.get(d.field)?.aggField || d.field
  if (d.op === 'date_histogram') {
    const iv = d.params?.interval && d.params.interval !== 'auto'
      ? d.params.interval
      : autoInterval(spanOf(d.field, ctx))
    return { date_histogram: { field, ...DATE_INTERVALS[iv], time_zone: TIME_ZONE, min_doc_count: 0 } }
  }
  if (d.op === 'histogram') {
    let interval = Number(d.params?.interval)
    if (!(interval > 0)) {
      const s = ctx.stats?.[d.field]
      interval = s && s.max != null ? niceStep((s.max - s.min) / 10 || 1) : 1
    }
    return { histogram: { field, interval, min_doc_count: 1 } }
  }
  const size = Math.max(1, Math.min(100, Number(d.params?.size) || 5))
  let order = { _count: 'desc' }
  if (d.params?.orderBy === 'key') order = { _key: 'asc' }
  else if (d.params?.orderBy === 'metric' && orderable) order = { m0: 'desc' }
  // Ordre porté par un AUTRE champ numérique du document (un rang : 1 mois,
  // 3 mois… 10 ans ; Tensions… Favorable) : le plus petit de chaque valeur.
  const orderField = d.params?.orderBy === 'field' && fmap.get(d.params?.orderField)?.kind === 'number'
    ? fmap.get(d.params.orderField).aggField
    : null
  if (orderField) {
    return { terms: { field, size, order: { o: d.params?.orderDir === 'desc' ? 'desc' : 'asc' } }, aggs: { o: { min: { field: orderField } } } }
  }
  return { terms: { field, size, order } }
}

function filters(ctx) {
  const out = []
  if (ctx.timeField && ctx.range && ctx.range !== 'all') {
    out.push({ range: { [ctx.timeField]: { gte: `now-${ctx.range}`, lte: 'now' } } })
  }
  const q = (ctx.query || '').trim()
  if (q) {
    out.push({ query_string: { query: q, default_operator: 'AND', lenient: true, analyze_wildcard: true } })
  }
  return out
}

/** Le filtre commun (période + barre de requête), réutilisé par les statistiques de champ. */
export const baseQuery = (ctx) => ({ bool: { filter: filters(ctx) } })

/**
 * La requête d'agrégation d'une visualisation.
 *
 * @param {object} viz  l'état de la visualisation.
 * @param {object} ctx  { fields, timeField, range, query, stats }.
 * @returns {object|null}  la DSL, ou null si la visualisation est incomplète.
 */
export function buildQuery(viz, ctx) {
  if (viz.type === 'list') {
    const cols = (viz.columns || []).filter((d) => d.field)
    if (!cols.length) return null
    return {
      size: Math.max(1, Math.min(200, Number(viz.size) || 50)),
      track_total_hits: true,
      query: baseQuery(ctx),
      _source: [...new Set(cols.map((d) => d.field))],
    }
  }
  const ms = metricsOf(viz)
  if (!ms.length || missingSlots(viz).length) return null
  const fmap = fieldMap(ctx.fields)
  const body = { size: 0, track_total_hits: true, query: baseQuery(ctx) }

  const metricAggs = (list) => {
    const out = {}
    list.forEach((m, i) => {
      const a = metricAgg(m, fmap, ctx)
      if (a) out[`m${i}`] = a
    })
    return out
  }
  const withAggs = (agg, sub) => {
    const all = { ...(agg.aggs || {}), ...sub }
    return Object.keys(all).length ? { ...agg, aggs: all } : agg
  }

  if (viz.type === 'metric') {
    const aggs = {}
    ms.forEach((m, i) => {
      // Le nombre d'enregistrements est lu comme les autres métriques, dans
      // `aggregations` : la réponse brute (`hits.total`) et l'enveloppe d'une
      // requête stockée (`metadata.total`) ne le rangent pas au même endroit.
      aggs[`m${i}`] = metricAgg(m, fmap, ctx) || { filter: { match_all: {} } }
    })
    return { ...body, aggs }
  }

  // Avec une ventilation, une seule métrique est représentée (une série par
  // valeur de la ventilation) — la première.
  const shown = viz.breakdown ? ms.slice(0, 1) : ms
  const first = shown[0]
  const inner = viz.breakdown
    ? { b: withAggs(bucketAgg(viz.breakdown, fmap, ctx, singleValue(first)), metricAggs(shown)) }
    : metricAggs(shown)
  // Sans axe : un seul bucket nommé « Total », que le `shape:` sait lire.
  const x = viz.x
    ? bucketAgg(viz.x, fmap, ctx, !viz.breakdown && singleValue(first))
    : { filters: { filters: { Total: { match_all: {} } } } }
  return { ...body, aggs: { x: withAggs(x, inner) } }
}

/* ------------------------------------------------------------------ *
 * Bloc reactive-json (ChartLoader)
 * ------------------------------------------------------------------ */

/**
 * Les props du `ChartLoader` qui rend la visualisation — sans sa source
 * (`data:` dans l'aperçu, `url:` dans l'export).
 */
export function buildBlock(viz, fields) {
  const ms = metricsOf(viz)
  const title = vizTitle(viz, fields)
  const base = { ariaLabel: title, emptyText: 'Aucun résultat sur la période' }

  if (viz.type === 'list') {
    const fmap = fieldMap(fields)
    return {
      ...base,
      variant: 'rows',
      columns: (viz.columns || []).filter((d) => d.field).map((d) => ({
        label: dimLabel(d, fields),
        path: d.field,
        // Format d'affichage : celui du genre du champ, sauf choix contraire
        // (une année, un code numérique se lisent en texte, pas « 2 005 »).
        ...((d.params?.format || (fmap.get(d.field)?.kind === 'number' ? 'number' : 'text')) === 'number' ? { format: 'number' } : {}),
      })),
      ...(viz.transpose ? { transpose: true } : { nowrap: true, sortable: true }),
    }
  }

  // Sans métrique complète (axe vertical vidé, champ pas encore choisi), rien à
  // représenter : l'éditeur affiche « Configuration incomplète ».
  if (!ms.length) return base

  if (viz.type === 'metric') {
    return {
      ...base,
      variant: 'kpis',
      cardChrome: ms.length > 1,
      kpiCards: ms.map((m, i) => ({
        label: dimLabel(m, fields),
        path: `aggregations.${m.op === 'count' ? `m${i}.doc_count` : metricPath(m, i)}`,
        format: 'number',
        decimals: decimalsOf(m, fields),
      })),
    }
  }

  const shown = viz.breakdown ? ms.slice(0, 1) : ms
  const shape = { buckets: 'aggregations.x.buckets' }
  const round = Math.max(...shown.map((m) => decimalsOf(m, fields)))
  if (round > 0) shape.round = round
  if (viz.breakdown) {
    shape.value = metricPath(shown[0], 0)
    shape.series = { from: 'b', stacked: viz.type === 'bar' || viz.type === 'bar_h' }
    if (viz.breakdown.params?.other && viz.breakdown.op === 'terms' && shown[0].op === 'count') {
      shape.series.otherLabel = 'Autres'
    }
  }
  else if (shown.length > 1) {
    shape.series = { metrics: shown.map((m, i) => ({ key: `m${i}`, label: dimLabel(m, fields), value: metricPath(m, i) })) }
  }
  else {
    shape.value = metricPath(shown[0], 0)
    shape.seriesLabel = dimLabel(shown[0], fields)
  }
  const multiSeries = Boolean(viz.breakdown) || shown.length > 1

  if (viz.type === 'table') {
    return {
      ...base,
      variant: 'table',
      shape,
      labelHeader: viz.x ? dimLabel(viz.x, fields) : ' ',
      valueHeader: dimLabel(shown[0], fields),
      // Un total de moyennes ne veut rien dire.
      totalRowLabel: shown.every(isAdditive) ? 'Total' : 'none',
      nowrap: true,
    }
  }

  const block = {
    ...base,
    shape,
    chartType: viz.type === 'bar_h' ? 'bar' : viz.type,
    richTooltip: true,
    exportable: true,
    exportName: 'lens',
    legendPosition: viz.type === 'doughnut' ? 'right' : (multiSeries ? 'bottom' : 'none'),
  }
  if (viz.type === 'bar_h') block.horizontal = true
  // Courbes sans points, trait épais : une série de centaines de dates se lit
  // comme une ligne, pas comme un chapelet (le point reparaît au survol).
  if (viz.type === 'line') {
    block.pointRadius = 0
    block.lineWidth = 3
    // Une série temporelle se lit autour de ses valeurs, pas depuis zéro.
    block.beginAtZero = false
    // Une courbe ne plonge pas à zéro là où une série n'a pas de valeur
    // (avant la création d'un fonds) : elle s'interrompt.
    if (shape.series) shape.series.gaps = true
    else shape.gaps = true
    // Chaque série repart de 100 au début de la période : on compare des
    // évolutions (un fonds, son indice), pas des niveaux.
    if (viz.rebase) {
      block.rebase = 100
      // Autour de 100, pas depuis 0 : les écarts entre séries se lisent.
      block.beginAtZero = false
    }
  }
  if (viz.type === 'doughnut') shape.colorPerPoint = true
  if (shape.series?.stacked) block.stacked = true
  if (multiSeries) block.fadeOnHover = true
  if (viz.x) block[viz.type === 'bar_h' ? 'yAxisTitle' : 'xAxisTitle'] = viz.type === 'doughnut' ? undefined : dimLabel(viz.x, fields)
  const tickUnit = viz.x?.op === 'date_histogram' ? viz.x.params?.tickUnit : null
  if (['line', 'bar'].includes(viz.type) && tickUnit && tickUnit !== 'auto' && TICK_UNITS[tickUnit]) block.xTickUnit = tickUnit
  if (viz.type !== 'doughnut' && !multiSeries) block[viz.type === 'bar_h' ? 'xAxisTitle' : 'yAxisTitle'] = dimLabel(shown[0], fields)
  return JSON.parse(JSON.stringify(block))
}

/** Le bloc à coller dans une page / un tableau de bord, branché sur une requête stockée. */
export function exportBlock(viz, fields, index, queryUuid) {
  return {
    type: 'ChartLoader',
    url: queryUuid
      ? `/api/elasticsearch/${index}/node-by-uuid/${queryUuid}`
      : `/api/elasticsearch/${index}/node-by-uuid/<uuid de la requête enregistrée>`,
    ...buildBlock(viz, fields),
  }
}

/**
 * Suggestions : les autres types qui se rendent avec LA MÊME requête — leur
 * aperçu réutilise donc la réponse déjà chargée, sans aucun appel de plus.
 */
export function suggestions(viz, ctx) {
  const current = JSON.stringify(buildQuery(viz, ctx))
  if (current === 'null') return []
  return Object.keys(TYPES)
    .filter((t) => t !== viz.type)
    .map((t) => ({ type: t, viz: switchType(viz, t, ctx.timeField) }))
    .filter((s) => !missingSlots(s.viz).length && JSON.stringify(buildQuery(s.viz, ctx)) === current)
}

/* ------------------------------------------------------------------ *
 * Erreurs
 * ------------------------------------------------------------------ */

/**
 * Message lisible d'une requête refusée : la route brute rend
 * `{error, message}` où `message` embarque la réponse d'Elasticsearch.
 */
export function errorMessage(body, status) {
  const raw = String(body?.message || body?.error || '')
  const reasons = [...raw.matchAll(/"reason":"((?:[^"\\]|\\.)*)"/g)].map((m) => m[1])
  // La PREMIÈRE raison est la cause racine, courte (« Failed to parse query
  // [x] ») ; les suivantes détaillent la grammaire attendue, sur 20 lignes.
  if (reasons.length) return reasons[0].replace(/\\"/g, '"').split('\\n')[0]
  if (status === 403) return "Accès refusé à cet index."
  return raw || `La requête a échoué (HTTP ${status}).`
}

/* ------------------------------------------------------------------ *
 * Assistant IA : l'état échangé avec le modèle
 * ------------------------------------------------------------------ */

/**
 * L'état courant, sous la forme que décrit le schéma de sortie de l'assistant
 * (orbit_lens LensAssistant::outputSchema) : sans identifiants, paramètres à plat.
 */
export function toAiState({ viz, timeField, range, query }) {
  const dim = (d) => d && {
    op: d.op,
    field: d.field ?? null,
    interval: d.params?.interval ?? null,
    tickUnit: d.params?.tickUnit ?? null,
    size: d.params?.size ?? null,
    orderBy: d.params?.orderBy ?? null,
    orderField: d.params?.orderField ?? null,
    orderDir: d.params?.orderDir ?? null,
    other: d.params?.other ?? null,
    percentile: d.op === 'pct' ? (d.params?.p ?? 95) : null,
    label: d.label || '',
  }
  return {
    type: viz.type,
    rebase: Boolean(viz.rebase),
    x: dim(viz.x) || null,
    y: (viz.y || []).map(dim),
    breakdown: dim(viz.breakdown) || null,
    timeField: timeField ?? null,
    range: range || 'all',
    query: query || '',
  }
}

/**
 * L'état proposé par l'assistant, VALIDÉ contre les champs de l'index : ce qui
 * ne tient pas (champ inconnu ou non agrégeable, fonction incompatible avec le
 * genre du champ) est écarté et signalé, jamais appliqué tel quel.
 *
 * @returns {{viz, timeField, range, query, warnings: string[]}}
 */
export function fromAiState(raw, fields) {
  const fmap = fieldMap(fields)
  const warnings = []
  const dim = (d, kind) => {
    if (!d || typeof d !== 'object') return null
    const ops = kind === 'metric' ? METRIC_OPS : BUCKET_OPS
    const op = ops[d.op]
    if (!op) {
      warnings.push(`Fonction « ${d.op} » inattendue à cet emplacement : ignorée.`)
      return null
    }
    let field = null
    if (op.kinds) {
      const f = fmap.get(d.field)
      if (!isAggregatable(f) || !op.kinds.includes(f.kind)) {
        warnings.push(`Champ « ${d.field} » inutilisable pour « ${op.label} » : ignoré.`)
        return null
      }
      field = f.name
    }
    const params = {}
    if (d.op === 'date_histogram') {
      params.interval = INTERVALS[d.interval] ? d.interval : 'auto'
      if (TICK_UNITS[d.tickUnit] && d.tickUnit !== 'auto') params.tickUnit = d.tickUnit
    }
    if (d.op === 'histogram') params.interval = Number(d.interval) > 0 ? Number(d.interval) : 'auto'
    if (d.op === 'terms') {
      params.size = Math.max(1, Math.min(100, Number(d.size) || 5))
      params.orderBy = ['count', 'metric', 'key'].includes(d.orderBy) ? d.orderBy : 'count'
      if (d.orderBy === 'field' && fmap.get(d.orderField)?.kind === 'number') {
        params.orderBy = 'field'
        params.orderField = d.orderField
        params.orderDir = d.orderDir === 'desc' ? 'desc' : 'asc'
      }
      params.other = Boolean(d.other)
    }
    if (d.op === 'pct') params.p = Math.max(1, Math.min(99, Number(d.percentile) || 95))
    const out = mk(d.op, field, params)
    if (typeof d.label === 'string' && d.label.trim()) out.label = d.label.trim()
    return out
  }

  const type = TYPES[raw?.type] ? raw.type : 'bar'
  let viz = {
    type,
    x: dim(raw?.x, 'bucket'),
    y: (Array.isArray(raw?.y) ? raw.y : []).map((d) => dim(d, 'metric')).filter(Boolean),
    breakdown: dim(raw?.breakdown, 'bucket'),
  }
  if (type === 'line' && raw?.rebase === true) viz.rebase = true
  if (type === 'metric') viz = { ...viz, x: null, breakdown: null }
  if (type === 'doughnut') viz = { ...viz, y: viz.y.slice(0, 1), breakdown: null }
  if (!viz.y.length) viz.y = [mk('count', null)]

  const tf = fmap.get(raw?.timeField)
  const timeField = tf?.kind === 'date' ? tf.name : null
  const range = timeField && RANGES[raw?.range] ? raw.range : 'all'
  return { viz, timeField, range, query: typeof raw?.query === 'string' ? raw.query : '', warnings }
}
